import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar } from '@capacitor/status-bar'
import { Server } from '@healthblocks-io/core'
import { useCallback, useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import Modal from 'react-modal'
import { Outlet, useLocation } from 'react-router-dom'

import { mixpanel } from '@/mixpanel'
import { useEdgeInsets } from './@bothrs-ui/web/hooks'
import { Cookiebot } from './Cookiebot'
import { AppUpdateMonitor } from './capacitor/components/app-update/AppUpdateMonitor'
import { WebPlatform } from './capacitor/components/native-platform/NativePlatform'
import { NetworkConnected } from './modules/app/pages/network/NetworkConnected'
import { CypressTestId } from './modules/cypress/components/cypress-test-id/CypressTestId'
import { servers } from './modules/healthblocks/ServerProvider'
import { DeeplinkListener } from './modules/shared/deep-links/DeeplinkListener'

Modal.setAppElement('#react-root')

const AppRoot = () => {
  const { bottom } = useEdgeInsets()
  const location = useLocation()

  const addPushListener = useCallback(async () => {
    await PushNotifications.addListener('pushNotificationActionPerformed', () => {
      mixpanel.track('push_notification_interaction')
    })
  }, [])

  useEffect(() => {
    // App is ready!
    if (Capacitor.isNativePlatform()) {
      // iOS renders an opaque status bar unless the webview overlays it. Let the
      // webview draw underneath so the bar stays transparent; Android keeps its
      // configured opaque, coloured bar.
      if (Capacitor.getPlatform() === 'ios') {
        StatusBar.setOverlaysWebView({ overlay: true })
      }
      addPushListener()
      setTimeout(() => {
        SplashScreen.hide({ fadeOutDuration: 500 })
      }, 250)
    }

    return () => {
      if (Capacitor.isNativePlatform()) {
        PushNotifications.removeAllListeners()
      }
    }
  }, [])

  useEffect(() => {
    const path = location.pathname
    mixpanel.track('page_view', { path })
  }, [location.pathname])

  // Helper Methods
  // ------------------------------------------------------------------------- /
  const handleUpdateAvailable = useCallback((currentVersion: string, availableVersion?: string) => {
    console.info('Current:', currentVersion)
    console.info('Available:', availableVersion)
  }, [])

  return (
    <NetworkConnected>
      <Server.Provider value={servers[0]}>
        {Capacitor.isNativePlatform() ? (
          <>
            <AppUpdateMonitor onUpdateAvailable={handleUpdateAvailable} />
            <DeeplinkListener />
          </>
        ) : null}

        <WebPlatform>
          <Cookiebot />
        </WebPlatform>
        <CypressTestId id='toast-container'>
          <Toaster
            containerStyle={{
              bottom: bottom + 90,
            }}
          />
        </CypressTestId>

        <Outlet />
      </Server.Provider>
    </NetworkConnected>
  )
}

export { AppRoot }
